import { Divider } from "antd";
import classNames from "classnames/bind";
import { FC, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../app/store";
import { SystemIcon } from "../../assets";
import { LMDropdown, LoaderWrapper } from "../../components";
import { Label } from "../../components/Typography";
import styles from "./SystemsSelector.module.css";
import { fetchSystem } from "./slice";

const cx = classNames.bind(styles);

interface Props {
  collapsed: boolean;
}

export const SystemsSelector: FC<Props> = ({ collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { t } = useTranslation("layout");
  const dispatch = useDispatch<AppDispatch>();

  const { status, systems, selectedSystem } = useSelector(
    (state: RootState) => {
      return state.systems;
    }
  );

  const handleChange = async (systemId: string) => {
    const newPath = location.pathname.replace(
      new RegExp(`no-system|${params.systemId}`),
      systemId.toString()
    );
    navigate(newPath, { replace: true });
  };

  useEffect(() => {
    if (!selectedSystem?.id) return;
    const interval = setInterval(() => {
      dispatch(fetchSystem({ systemId: selectedSystem.id }));
    }, 5000);
    return () => {
      clearInterval(interval);
    };
  }, [selectedSystem, selectedSystem?.id]);

  const title = t("system") as string;
  const shortTitle = title.slice(0, 3);

  const getValue = useCallback(() => {
    if (!selectedSystem) return;
    return selectedSystem.id.toString();
  }, [selectedSystem]);

  return (
    <LoaderWrapper status={status}>
      <>
        <div className={cx({ header: true, collapsed })}>
          <Divider className={styles.divider} />
          <Label className={cx({ title: true, collapsed })}>
            {collapsed ? shortTitle : title}
          </Label>
        </div>
        <div className={styles.container}>
          <LMDropdown
            Icon={({ className }) => <SystemIcon className={className} />}
            onChange={handleChange}
            items={systems.map((system) => ({
              name: system.name,
              value: system.id.toString(),
            }))}
            value={getValue()}
            title={t("chooseSystem")}
            collapsed={collapsed}
          />
        </div>
      </>
    </LoaderWrapper>
  );
};
